/**
 * Accessibility Utilities
 * Based on SHARED-ACCESSIBILITY.md
 */

/**
 * Keyboard Navigation Detection
 *
 * Adds/removes a class on the document body when the user is navigating via keyboard.
 * This allows CSS to show focus indicators only for keyboard users.
 *
 * Usage:
 * 1. Call setupKeyboardNavigation() once in your app initialization
 * 2. Use CSS: .keyboard-navigation *:focus { ... }
 */

let isKeyboardNavigating = false
let hasSetup = false

export function setupKeyboardNavigation() {
  if (typeof window === 'undefined' || hasSetup) return

  const KEYBOARD_NAV_CLASS = 'keyboard-navigation'

  // Detect keyboard navigation
  const handleKeyDown = (e: KeyboardEvent) => {
    // Only Tab key indicates keyboard navigation intent
    if (e.key === 'Tab') {
      if (!isKeyboardNavigating) {
        isKeyboardNavigating = true
        document.body.classList.add(KEYBOARD_NAV_CLASS)
      }
    }
  }

  // Mouse click indicates mouse navigation
  const handleMouseDown = () => {
    if (isKeyboardNavigating) {
      isKeyboardNavigating = false
      document.body.classList.remove(KEYBOARD_NAV_CLASS)
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('mousedown', handleMouseDown)

  hasSetup = true

  // Cleanup function (for testing or SPA unmount)
  return () => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('mousedown', handleMouseDown)
    document.body.classList.remove(KEYBOARD_NAV_CLASS)
    hasSetup = false
  }
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Hook-based reduced motion check
 */
import { useState, useEffect } from 'react'

export function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReduced(event.matches)
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Older browsers
    else {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }
  }, [])

  return prefersReduced
}

/**
 * Generate unique ID for accessibility attributes
 */
let idCounter = 0
export function generateId(prefix = 'id'): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

/**
 * Safely get element by ID (handles server-side rendering)
 */
export function getElementById(id: string): HTMLElement | null {
  if (typeof document === 'undefined') return null
  return document.getElementById(id)
}

/**
 * Focus management utilities
 */

/**
 * Set focus to an element after a delay (useful for animations)
 */
export function focusElement(
  element: HTMLElement | null,
  delay = 0
): void {
  if (!element) return

  if (delay > 0) {
    setTimeout(() => element.focus(), delay)
  } else {
    element.focus()
  }
}

/**
 * Get all focusable elements within a container
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')

  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelector)
  ).filter((el) => el.offsetParent !== null)
}

/**
 * Check if element is focusable
 */
export function isFocusable(element: Element): boolean {
  if (!(element instanceof HTMLElement)) return false

  // Check if element is disabled
  if (
    element.hasAttribute('disabled') ||
    element.getAttribute('aria-disabled') === 'true'
  ) {
    return false
  }

  // Check tabindex
  const tabIndex = element.getAttribute('tabindex')
  if (tabIndex === '-1') return false

  // Check if element is naturally focusable
  const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']
  if (focusableTags.includes(element.tagName)) {
    // Links need href to be focusable
    if (element.tagName === 'A' && !element.hasAttribute('href')) {
      return false
    }
    return true
  }

  // Check for tabindex >= 0
  return tabIndex !== null && parseInt(tabIndex, 10) >= 0
}
